import { IconDots } from "@tabler/icons-react";
import { MouseEventHandler, PropsWithChildren, ReactNode } from "react";

import { Button, ButtonProps, Flex, Menu, type MenuItemProps, type MenuProps, mantineColorRamps, Text } from "~/ui";

import { OrgLink } from "./OrgLink";

type StyleMenuProps = {
  keepMounted?: MenuProps["keepMounted"];
  position?: MenuProps["position"];
  withinPortal?: MenuProps["withinPortal"];
  button?: JSX.Element;
  buttonColor?: ButtonProps["color"];
  buttonLabel?: JSX.Element | string;
  buttonVariant?: ButtonProps["variant"];
  buttonLeftIcon?: ButtonProps["leftIcon"];
  buttonRightIcon?: ButtonProps["rightIcon"];
  loading?: ButtonProps["loading"];
  /** Dropdown layer; a menu opened inside a drawer or modal passes 300, the Menu default above their 200. */
  dropdownZIndex?: number;
};

type StyledMenuItemProps = {
  color?: MenuItemProps["color"];
  disabled?: boolean;
  href?: string;
  icon?: ReactNode;
  onClick?: MouseEventHandler<HTMLButtonElement>;
};

const menuTransition = {
  in: { opacity: 1, transform: "translateY(0)" },
  out: { opacity: 0, transform: "translateY(-1rem)" },
  common: { transformOrigin: "top" },
  transitionProperty: "transform, opacity",
};

const StyledMenuItem = (props: PropsWithChildren<StyledMenuItemProps>): JSX.Element => {
  const menuItemColor = props.color ?? mantineColorRamps.gray[9];

  return (
    <Menu.Item
      color={menuItemColor}
      disabled={props.disabled}
      fz="sm"
      py="sm"
      px="xl"
      onClick={props.onClick}
      style={{ position: "relative", zIndex: 2 }}
    >
      {props.href ? (
        <OrgLink href={props.href}>
          {props.icon}
          <Text color={menuItemColor}>{props.children}</Text>
        </OrgLink>
      ) : (
        <Flex align="center" direction="row" gap="xs">
          {props.icon}
          {props.children}
        </Flex>
      )}
    </Menu.Item>
  );
};

export const StyledMenu = (props: PropsWithChildren<StyleMenuProps>): JSX.Element => {
  return (
    <Menu
      arrowOffset={10}
      arrowPosition="side"
      arrowSize={12}
      keepMounted={props.keepMounted}
      position={props.position ?? "bottom-end"}
      shadow="md"
      transitionProps={{ transition: menuTransition, duration: 150 }}
      withArrow
      // Sticky datatable headers sit at z-index 2; an inline dropdown at that layer is painted over.
      withinPortal={props.withinPortal ?? true}
    >
      <Menu.Target>
        {props.button ?? (
          <Button
            color={props.buttonColor}
            leftIcon={props.buttonLeftIcon}
            loading={props.loading}
            p="sm"
            rightIcon={props.buttonRightIcon}
            variant={props.buttonVariant ?? "outline"}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            {props.buttonLabel ? props.buttonLabel : <IconDots size={16} />}
          </Button>
        )}
      </Menu.Target>
      <Menu.Dropdown
        style={{
          border: `1px solid ${mantineColorRamps.gray[3]}`,
          zIndex: props.dropdownZIndex ?? 2,
        }}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        {props.children}
      </Menu.Dropdown>
    </Menu>
  );
};

StyledMenu.Divider = Menu.Divider;
StyledMenu.Item = StyledMenuItem;
StyledMenu.Label = Menu.Label;
